import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MessageSquare, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/providers/AuthProvider';

interface MemberFeedbackDialogProps {
  trigger?: React.ReactNode;
}

const feedbackCategories = [
  { value: 'bug', label: 'Report a Bug/Issue', description: 'Something is not working correctly' },
  { value: 'improvement', label: 'Suggest an Improvement', description: 'Make existing features better' },
  { value: 'feature', label: 'Request a New Feature', description: 'Add something new to the platform' },
  { value: 'ui_ux', label: 'UI/UX Feedback', description: 'Design and user experience feedback' },
  { value: 'performance', label: 'Performance Issue', description: 'Site speed or loading concerns' },
  { value: 'general', label: 'General Feedback', description: 'Other feedback or comments' },
];

export default function MemberFeedbackDialog({ trigger }: MemberFeedbackDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast({
        title: 'Category Required',
        description: 'Please select a feedback category',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userId = authUser?.id || user?.id || null;

      // Get user profile data if available
      let userEmail = authUser?.email || 'anonymous@feedback.com';
      let userName = 'Anonymous User';

      if (userId) {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('email, first_name, last_name')
            .eq('id', userId as any)
            .maybeSingle();

          if (profile) {
            userEmail = (profile as any).email || userEmail;
            const firstName = (profile as any).first_name;
            const lastName = (profile as any).last_name;
            if (firstName || lastName) {
              userName = [firstName, lastName].filter(Boolean).join(' ');
            }
          }
        } catch (err) {
          // If profile fetch fails, continue with default values
          console.error('Profile fetch error:', err);
        }
      }

      const categoryLabel = feedbackCategories.find(c => c.value === selectedCategory)?.label || selectedCategory;

      const { error } = await supabase
        .from('form_submissions')
        .insert([{
          form_type: 'member_feedback',
          email: userEmail,
          message: feedbackText || 'No additional details provided',
          status: 'pending',
          user_id: userId,
          metadata: {
            category: selectedCategory,
            category_label: categoryLabel,
            user_name: userName,
            submitted_from: 'home_banner',
            timestamp: new Date().toISOString(),
          },
        }] as any);

      if (error) throw error;

      setSubmitted(true);
      toast({
        title: 'Feedback Submitted! 🎉',
        description: 'Thank you for helping us improve. We\'ll review your feedback soon.',
      });

      // Reset form after 2 seconds and close dialog
      setTimeout(() => {
        setSelectedCategory('');
        setFeedbackText('');
        setSubmitted(false);
        setOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Feedback submission error:', error);
      const description = error instanceof Error ? error.message : 'Failed to submit feedback';
      toast({
        title: 'Submission Failed',
        description,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const defaultTrigger = (
    <Button 
      size="lg"
      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-light tracking-wide rounded-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
    >
      <MessageSquare className="h-5 w-5 mr-2" />
      Share Your Feedback
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border-white/20">
        <DialogHeader>
          <DialogTitle className="text-2xl font-medium flex items-center gap-2 text-white">
            <div className="relative">
              <div className="p-2 bg-gradient-to-br from-emerald-500/20 via-teal-500/20 to-blue-500/20 rounded-xl border border-white/20">
                <MessageSquare className="h-6 w-6 text-emerald-400" />
              </div>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 blur-lg opacity-50" />
            </div>
            Member Feedback
          </DialogTitle>
          <DialogDescription className="text-base text-white/70">
            Help us improve your experience! Select a category and share your thoughts.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-12 text-center">
            <div className="relative inline-block">
              <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 blur-xl" />
            </div>
            <h3 className="text-xl font-medium mb-2 text-white">Thank You!</h3>
            <p className="text-white/70">Your feedback has been received.</p>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Category Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-white">What would you like to share?</Label>
              <RadioGroup value={selectedCategory} onValueChange={setSelectedCategory}>
                <div className="grid gap-3">
                  {feedbackCategories.map((category) => (
                    <label
                      key={category.value}
                      className={`
                        flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all backdrop-blur-sm
                        ${
                          selectedCategory === category.value
                            ? 'border-emerald-500 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-blue-500/10 shadow-lg'
                            : 'border-white/10 bg-white/5 hover:border-emerald-500/50 hover:bg-white/10'
                        }
                      `}
                    >
                      <RadioGroupItem value={category.value} id={category.value} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-white">{category.label}</div>
                        <div className="text-xs text-white/60 mt-0.5 font-light">{category.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </RadioGroup>
            </div>

            {/* Additional Details */}
            <div className="space-y-2">
              <Label htmlFor="feedback-text" className="text-base font-medium text-white">
                Additional Details <span className="text-white/60 font-light">(Optional)</span>
              </Label>
              <Textarea
                id="feedback-text"
                placeholder="Tell us more about your feedback... What happened? What would you like to see? Any specific examples?"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={6}
                className="resize-none bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-500/50 focus:ring-emerald-500/20"
              />
              <p className="text-xs text-white/60 font-light">
                {user ? `Submitting as ${user.email}` : 'You can submit feedback anonymously'}
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="border-white/20 text-white/80 hover:bg-white/10 hover:text-white font-light"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !selectedCategory}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-light tracking-wide shadow-lg hover:shadow-xl"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
